import { useMsal } from '@azure/msal-react';
import { useCobaltLayoutContext } from '@cae/cobalt-react/components/layout';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { useOrganizations } from '@contexts/organizations-provider';
import { usePermissions } from '@contexts/permissions-provider';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { useGetToken } from '@shared/hooks/use-get-token';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { EtcaRouterProps } from './EtcaRouterPage.types';

/**
 * EtcaRouterPage - MFE wrapper for eTCA
 *
 * Loads the remote ETcaMFE component and passes the required props.
 * The trainingEventId is extracted from the named route param `:trainingEventId`
 * captured by the `etca/:trainingEventId` route definition.
 *
 * Guard stack (applied in manage.routes.tsx):
 * 1. AuthenticatedRoute  - User must be logged in
 * 2. OrganizationRouteGuard - Validate org access and auto-select
 * 3. FeatureFlagGuard    - etcaPage must be enabled
 * 4. PermissionGuard     - Reservations_Edit_Org required
 *
 * @see docs/ROUTE_ARCHITECTURE.md for guard documentation
 */
const EtcaRouterPage = React.memo((): React.ReactElement | null => {
  const { instance } = useMsal();
  const authData = useAuthenticatedUser();
  const { permissions } = usePermissions();
  const { hideSideNavigation } = useCobaltLayoutContext();
  const { selectedOrganization } = useOrganizations();
  const getToken = useGetToken();

  const { trainingEventId } = useParams<{ trainingEventId: string }>();
  const navigate = useNavigate();

  const handleNavigateBack = React.useCallback((): void => {
    navigate(`/manage/${selectedOrganization?.customerId}/reservations`);
  }, [navigate, selectedOrganization?.customerId]);

  const [RemoteEtca, setRemoteEtca] =
    React.useState<React.ComponentType<EtcaRouterProps> | null>(null);
  const [loadError, setLoadError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    const loadRouter = async (): Promise<void> => {
      try {
        const { default: Router } = await import('etca_mfe/ETCA');
        setRemoteEtca(() => Router);
      } catch (error) {
        console.error('Error loading ETCA:', error);
        setLoadError(error as Error);
      }
    };

    loadRouter();
  }, []);

  if (loadError) {
    return <InlineFeedback type="internal-server-error" showGoHome={false} />;
  }

  if (!trainingEventId || !authData || !RemoteEtca || !selectedOrganization) {
    return <ContentLoader />;
  }

  return (
    <React.Suspense fallback={<ContentLoader />}>
      <RemoteEtca
        msalInstance={instance}
        user={authData}
        trainingEventId={trainingEventId}
        permissions={permissions}
        hideSideNavigation={hideSideNavigation}
        onNavigateBack={handleNavigateBack}
        getToken={getToken}
      />
    </React.Suspense>
  );
});

EtcaRouterPage.displayName = 'EtcaRouterPage';

export { EtcaRouterPage };
